import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Activity, ArrowRight, Calendar, ClipboardList, Target, Compass, Bell, BarChart3, Brain, Youtube, Video, Lock } from 'lucide-react';
import { getScoreInterpretation } from '../data/fagerstromQuestions';
import { getMotivationInterpretation } from '../data/motivationQuestions';
import { getStageFromScore } from '../data/prochaskaQuestions';
import { ProgressCard } from '../components/ProgressCard';
import { HealthTimelineCard } from '../components/HealthTimelineCard';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [latestFagerstrom, setLatestFagerstrom] = useState(null);
  const [latestMotivation, setLatestMotivation] = useState(null);
  const [latestProchaska, setLatestProchaska] = useState(null);
  const [latestTracking, setLatestTracking] = useState(null);
  const [trackingStats, setTrackingStats] = useState(null);
  const [progressStats, setProgressStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    fetchData();
    checkNotificationPermission();
  }, []);

  const fetchData = async () => {
    try {
      const [fagerstromRes, motivationRes, prochaskaRes, trackingRes, statsRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/fagerstrom/latest`),
        axios.get(`${API_URL}/motivation/latest`),
        axios.get(`${API_URL}/prochaska/latest`),
        axios.get(`${API_URL}/tracking/weekly/latest`),
        axios.get(`${API_URL}/tracking/stats`),
        axios.get(`${API_URL}/stats/progress`)
      ]);
      
      setLatestFagerstrom(fagerstromRes.data);
      setLatestMotivation(motivationRes.data);
      setLatestProchaska(prochaskaRes.data);
      setLatestTracking(trackingRes.data);
      setTrackingStats(statsRes.data);
      setProgressStats(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const enableNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success(language === 'fr' ? 'Notifications activées !' : 'Notifications enabled!');
        localStorage.setItem('weeklyReminderEnabled', 'true');
      }
    }
  };

  const fagerstromInterpretation = latestFagerstrom 
    ? getScoreInterpretation(latestFagerstrom.total_score, language)
    : null;

  const motivationInterpretation = latestMotivation 
    ? getMotivationInterpretation(latestMotivation.total_score, language)
    : null;

  const prochaskaStage = latestProchaska
    ? getStageFromScore(latestProchaska.total_score, language)
    : null;

  const needsWeeklyTracking = () => {
    if (!latestTracking) return true;
    const lastDate = new Date(latestTracking.week_start_date);
    const now = new Date();
    const daysDiff = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
    return daysDiff >= 7;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {t('welcome')}, {user?.first_name} !
          </h1>
          <p className="text-lg text-gray-600">
            {language === 'fr' 
              ? 'Suivez votre progression vers une vie sans tabac.'
              : 'Track your progress towards a smoke-free life.'}
          </p>
        </div>

        {/* Progress Card - Hero */}
        <ProgressCard stats={progressStats} />

        {/* Weekly Tracking Alert */}
        {needsWeeklyTracking() && (latestFagerstrom || latestMotivation) && (
          <Card className="mb-6 border-2 border-orange-200 bg-orange-50 rounded-3xl animate-fade-in-up">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-orange-800">
                      {language === 'fr' ? 'Suivi hebdomadaire requis' : 'Weekly tracking required'}
                    </h3>
                    <p className="text-sm text-orange-600">
                      {language === 'fr' 
                        ? 'Complétez votre suivi pour voir vos statistiques évoluer.'
                        : 'Complete your tracking to see your statistics evolve.'}
                    </p>
                  </div>
                </div>
                <Link to="/weekly-tracking">
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600 shadow-lg">
                    {language === 'fr' ? 'Faire le suivi' : 'Do tracking'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tests Grid - 4 colonnes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          
          {/* Fagerström Card */}
          <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-lg animate-fade-in-up" data-testid="fagerstrom-card">
            {latestFagerstrom ? (
              <div className={`h-full ${fagerstromInterpretation.bgColor} p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <Activity className={`w-4 h-4 ${fagerstromInterpretation.textColor}`} />
                  <span className={`font-medium text-xs ${fagerstromInterpretation.textColor}`}>Fagerström</span>
                </div>
                <div className={`text-4xl font-extrabold ${fagerstromInterpretation.textColor} mb-1`}>
                  {latestFagerstrom.total_score}<span className="text-lg opacity-60">/10</span>
                </div>
                <p className={`text-xs ${fagerstromInterpretation.textColor} mb-3`}>{fagerstromInterpretation.level}</p>
                <Link to="/questionnaire">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white/80 hover:bg-white text-gray-800 text-xs h-8">
                    {language === 'fr' ? 'Refaire' : 'Retake'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="h-full bg-primary p-5 flex flex-col justify-center items-center text-white">
                <ClipboardList className="w-10 h-10 mb-2 opacity-80" />
                <h3 className="text-sm font-bold mb-1">Fagerström</h3>
                <p className="text-xs opacity-80 mb-3 text-center">{language === 'fr' ? 'Dépendance' : 'Dependence'}</p>
                <Link to="/questionnaire">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white text-primary text-xs h-8">
                    {language === 'fr' ? 'Commencer' : 'Start'}
                  </Button>
                </Link>
              </div>
            )}
          </Card>

          {/* Motivation Card */}
          <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-lg animate-fade-in-up animate-delay-100" data-testid="motivation-card">
            {latestMotivation ? (
              <div className={`h-full ${motivationInterpretation.bgColor} p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <Target className={`w-4 h-4 ${motivationInterpretation.textColor}`} />
                  <span className={`font-medium text-xs ${motivationInterpretation.textColor}`}>Motivation</span>
                </div>
                <div className={`text-4xl font-extrabold ${motivationInterpretation.textColor} mb-1`}>
                  {latestMotivation.total_score}<span className="text-lg opacity-60">/16</span>
                </div>
                <p className={`text-xs ${motivationInterpretation.textColor} mb-3`}>{motivationInterpretation.level}</p>
                <Link to="/motivation-test">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white/80 hover:bg-white text-gray-800 text-xs h-8">
                    {language === 'fr' ? 'Refaire' : 'Retake'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="h-full bg-purple-600 p-5 flex flex-col justify-center items-center text-white">
                <Target className="w-10 h-10 mb-2 opacity-80" />
                <h3 className="text-sm font-bold mb-1">Motivation</h3>
                <p className="text-xs opacity-80 mb-3 text-center">{language === 'fr' ? 'Évaluation' : 'Assessment'}</p>
                <Link to="/motivation-test">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white text-purple-600 text-xs h-8">
                    {language === 'fr' ? 'Commencer' : 'Start'}
                  </Button>
                </Link>
              </div>
            )}
          </Card>

          {/* Prochaska Card */}
          <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-lg animate-fade-in-up animate-delay-200" data-testid="prochaska-card">
            {latestProchaska ? (
              <div className={`h-full ${prochaskaStage.color} p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <Compass className={`w-4 h-4 ${prochaskaStage.textColor}`} />
                  <span className={`font-medium text-xs ${prochaskaStage.textColor}`}>Prochaska</span>
                </div>
                <div className={`text-xl font-extrabold ${prochaskaStage.textColor} mb-1`}>
                  {language === 'fr' ? prochaskaStage.name_fr : prochaskaStage.name_en}
                </div>
                <p className={`text-xs ${prochaskaStage.textColor} opacity-80 mb-3`}>
                  {language === 'fr' ? 'Stade de changement' : 'Stage of change'}
                </p>
                <Link to="/prochaska-test">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white/80 hover:bg-white text-gray-800 text-xs h-8">
                    {language === 'fr' ? 'Refaire' : 'Retake'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="h-full bg-teal-600 p-5 flex flex-col justify-center items-center text-white">
                <Compass className="w-10 h-10 mb-2 opacity-80" />
                <h3 className="text-sm font-bold mb-1">Prochaska</h3>
                <p className="text-xs opacity-80 mb-3 text-center">{language === 'fr' ? 'Stade de changement' : 'Stage of change'}</p>
                <Link to="/prochaska-test">
                  <Button variant="secondary" size="sm" className="rounded-full bg-white text-teal-600 text-xs h-8">
                    {language === 'fr' ? 'Commencer' : 'Start'}
                  </Button>
                </Link>
              </div>
            )}
          </Card>

          {/* Statistics Card */}
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-5 animate-fade-in-up animate-delay-300" data-testid="stats-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-blue-600" />
              </div>
              <span className="font-medium text-xs text-gray-600">
                {language === 'fr' ? 'Statistiques' : 'Statistics'}
              </span>
            </div>
            
            {trackingStats?.total_weeks > 0 ? (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-gray-500">{language === 'fr' ? 'Cig/j' : 'Cig/d'}</p>
                  <p className="text-lg font-bold text-gray-900">{trackingStats.current_cigarettes}</p>
                </div>
                <div className="p-2 bg-purple-50 rounded-lg">
                  <p className="text-[10px] text-purple-600">{language === 'fr' ? 'Envie' : 'Craving'}</p>
                  <p className="text-lg font-bold text-purple-700">{trackingStats.avg_craving?.toFixed(1)}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 mb-3">{language === 'fr' ? 'Aucune donnée' : 'No data'}</p>
            )}
            
            <Link to="/statistics">
              <Button variant="outline" size="sm" className="w-full rounded-full text-xs h-8">
                {language === 'fr' ? 'Voir' : 'View'}
              </Button>
            </Link>
          </Card>
        </div>

        {/* Videos Tab - unlocked after all tests */}
        {(() => {
          const done = [latestFagerstrom, latestMotivation, latestProchaska].filter(Boolean).length;
          const unlocked = done === 3;
          return (
            <Card className={`mb-6 rounded-3xl border-0 shadow-lg animate-fade-in-up ${unlocked ? 'bg-red-50' : 'bg-gray-100'}`} data-testid="videos-tab-card">
              <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${unlocked ? 'bg-red-100' : 'bg-gray-200'}`}>
                    {unlocked ? <Video className="w-6 h-6 text-red-600" /> : <Lock className="w-6 h-6 text-gray-500" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{language === 'fr' ? 'Vidéos' : 'Videos'}</h3>
                    <p className="text-sm text-gray-600" data-testid="videos-tab-status">
                      {unlocked
                        ? (language === 'fr' ? 'Débloqué ! Découvrez nos vidéos conseils.' : 'Unlocked! Discover our video tips.')
                        : (language === 'fr' ? `Terminez les 3 tests pour débloquer (${done}/3)` : `Complete the 3 tests to unlock (${done}/3)`)}
                    </p>
                  </div>
                </div>
                {unlocked ? (
                  <Link to="/videos">
                    <Button className="rounded-full bg-red-600 hover:bg-red-700 shadow-lg" data-testid="videos-tab-open-btn">
                      {language === 'fr' ? 'Voir les vidéos' : 'Watch videos'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : (
                  <Button disabled className="rounded-full" data-testid="videos-tab-locked-btn">
                    <Lock className="mr-2 w-4 h-4" />
                    {language === 'fr' ? 'Verrouillé' : 'Locked'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Bottom Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Weekly Tracking Card */}
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-6 animate-fade-in-up" data-testid="weekly-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'fr' ? 'Suivi hebdomadaire' : 'Weekly tracking'}
                </h3>
                <p className="text-xs text-gray-500">5 questions</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'fr'
                ? 'Anxiété et envie calculées automatiquement.'
                : 'Anxiety and craving calculated automatically.'}
            </p>
            <Link to="/weekly-tracking">
              <Button className="w-full rounded-full bg-green-600 hover:bg-green-700 shadow-lg">
                {language === 'fr' ? 'Faire le suivi' : 'Start tracking'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </Card>

          {/* YouTube / Social Media Card */}
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-6 animate-fade-in-up animate-delay-100 relative overflow-hidden" data-testid="youtube-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
                <Youtube className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'fr' ? 'Nos réseaux sociaux' : 'Our social media'}
                </h3>
                <p className="text-xs text-gray-500">YouTube</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'fr'
                ? 'Découvrez notre chaîne YouTube et nos conseils vidéo.'
                : 'Discover our YouTube channel and video tips.'}
            </p>
            <a 
              href="https://www.youtube.com/channel/UC49QQgZVQHGncVp3znahkcQ" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://www.youtube.com/channel/UC49QQgZVQHGncVp3znahkcQ', '_blank', 'noopener,noreferrer');
              }}
              data-testid="youtube-link"
            >
              <Button className="w-full rounded-full bg-red-600 hover:bg-red-700 shadow-lg">
                <Youtube className="mr-2 w-4 h-4" />
                {language === 'fr' ? 'Voir la chaîne' : 'View channel'}
              </Button>
            </a>
          </Card>

          {/* Exercises Card */}
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-6 animate-fade-in-up animate-delay-200" data-testid="exercises-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                <Brain className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'fr' ? 'Exercices TCC' : 'CBT Exercises'}
                </h3>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Respiration' : 'Breathing'}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'fr'
                ? 'Techniques pour gérer les envies de fumer.'
                : 'Techniques to manage smoking cravings.'}
            </p>
            <Link to="/exercises">
              <Button variant="outline" className="w-full rounded-full border-2">
                {language === 'fr' ? 'Voir' : 'View'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </Card>

          {/* Notifications Card */}
          <Card className="glass-card rounded-3xl border-0 shadow-lg p-6 animate-fade-in-up animate-delay-300" data-testid="notifications-card">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${notificationsEnabled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <Bell className={`w-6 h-6 ${notificationsEnabled ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'fr' ? 'Rappels' : 'Reminders'}
                </h3>
                <p className="text-xs text-gray-500">
                  {notificationsEnabled 
                    ? (language === 'fr' ? 'Activés' : 'Enabled')
                    : (language === 'fr' ? 'Désactivés' : 'Disabled')}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'fr'
                ? 'Recevez un rappel hebdomadaire.'
                : 'Get a weekly reminder.'}
            </p>
            {!notificationsEnabled ? (
              <Button onClick={enableNotifications} className="w-full rounded-full bg-yellow-500 hover:bg-yellow-600">
                {language === 'fr' ? 'Activer' : 'Enable'}
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-green-600 justify-center">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-sm font-medium">{language === 'fr' ? 'Actif' : 'Active'}</span>
              </div>
            )}
          </Card>
        </div>

        {/* Health Benefits Timeline */}
        <div className="mt-6">
          <HealthTimelineCard daysOnJourney={progressStats?.days_on_journey || 0} />
        </div>
      </div>
    </div>
  );
};
